import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const TIER_DATA = {
  saas: {
    label: 'SaaS — self-serve',
    description: 'You run it. Pay monthly. Scale up when you need to.',
    tiers: [
      {
        key: 'starter',
        name: 'Starter',
        price: 299,
        tagline: 'For small teams getting started',
        highlights: ['1,000 credits per month', '20,000 contacts', '2 users, 1 workspace', 'Pipedrive + Bigin sync', 'Daily signal sweep'],
      },
      {
        key: 'growth',
        name: 'Growth',
        price: 499,
        tagline: 'For scaling sales teams',
        highlights: ['3,000 credits per month', '50,000 contacts', '10 users, 5 workspaces', 'All major CRM syncs', 'HeyReach LinkedIn execution', 'Onboarding call'],
        popular: true,
      },
      {
        key: 'agency',
        name: 'Agency',
        price: 699,
        tagline: 'For agencies and large teams',
        highlights: ['6,000 credits per month', 'Unlimited contacts/users/workspaces', 'Custom email domain', 'Co-branded reports', 'Priority support', 'Dedicated account manager'],
      },
    ],
  },
  managed_service: {
    label: 'Managed Service — we run it for you',
    description: 'White-glove. Webcite operates the engine on your behalf.',
    tiers: [
      {
        key: 'launchpad',
        name: 'Launchpad',
        price: 1200,
        tagline: 'Get your outbound engine launched',
        highlights: ['ICP and targeting strategy', '500 prospects researched', '3 domains + 15 mailboxes', 'First campaign launched', 'CRM set up', 'Monthly review call'],
      },
      {
        key: 'growth',
        name: 'Growth',
        price: 2000,
        tagline: 'Ongoing managed campaigns',
        highlights: ['200 new prospects/month', '7 domains + 35 mailboxes', 'Weekly sequence optimisation', 'Signal-triggered outreach', 'LinkedIn outreach', 'Monthly intelligence digest'],
        popular: true,
      },
      {
        key: 'partner',
        name: 'Partner',
        price: 3000,
        tagline: 'Full RevOps partnership',
        highlights: ['200+ new prospects/month', '10 domains + 70 mailboxes', 'Full RevOps', 'All major CRMs managed', 'Fortnightly pipeline review', 'Dedicated account manager'],
      },
    ],
  },
};

export default function Pricing({ embedded = false }) {
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const checkoutStatus = searchParams.get('status');

  const [tierData, setTierData] = useState(null);
  const [expandedTiers, setExpandedTiers] = useState({});
  useEffect(() => { load(); loadTiers(); }, []);
  const TRACK_ORDER = ['saas', 'managed_service'];
  const orderTracks = (data) => {
    const entries = Object.entries(data || {});
    return entries.sort((a, b) => {
      const ai = TRACK_ORDER.indexOf(a[0]); const bi = TRACK_ORDER.indexOf(b[0]);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  };

  async function loadTiers() {
    try {
      const r = await fetch(`${API}/pricing/tiers`);
      if (r.ok) setTierData(await r.json());
    } catch (_) {}
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API}/billing/current`, {
        headers: { 'Authorization': 'Bearer ' + token },
      });
      if (!r.ok) {
        const e = await r.json();
        throw new Error(e.error || 'Failed to load billing info');
      }
      const data = await r.json();
      setCurrent(data);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  }

  // If the customer already has a Stripe subscription, all plan changes go through
  // the Customer Portal so Stripe handles proration and avoids creating duplicate subs.
  // First-time subscribers go directly to checkout.
  async function handleTierAction(track, tier) {
    if (current?.has_stripe_customer) {
      return handlePortal();
    }
    return handleSubscribe(track, tier);
  }

  async function handleSubscribe(track, tier) {
    setActionLoading(track + '-' + tier);
    try {
      const token = await getToken();
      const r = await fetch(`${API}/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({ service_track: track, service_tier: tier }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Checkout failed');
      window.location.href = data.url;
    } catch (e) {
      alert('Could not start checkout: ' + e.message);
      setActionLoading(null);
    }
  }

  async function handlePortal() {
    setActionLoading('portal');
    try {
      const token = await getToken();
      const r = await fetch(`${API}/billing/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify({ return_url: window.location.href }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Portal session failed');
      window.location.href = data.url;
    } catch (e) {
      alert('Could not open billing portal: ' + e.message);
      setActionLoading(null);
    }
  }

  if (loading) return <div style={styles.loading}>Loading…</div>;

  const isCurrentTier = (track, tier) =>
    current?.service_track === track && current?.service_tier === tier;
  const hasSubscription = !!current?.has_stripe_customer;

  return (
    <div style={embedded ? { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' } : styles.page}>
      <div style={embedded ? {} : styles.maxWidth}>
        <header style={styles.header}>
          <h1 style={styles.h1}>Plans &amp; pricing</h1>
          <p style={styles.subhead}>Choose the plan that fits where you are. Upgrade, downgrade, or cancel anytime.</p>
        </header>

        {checkoutStatus === 'success' && (
          <div style={styles.banner('#0b6e3f', '#e7f6ec')}>
            Payment confirmed. Your new plan will be active shortly.
          </div>
        )}
        {checkoutStatus === 'cancel' && (
          <div style={styles.banner('#8B1A1A', '#fcecec')}>
            Checkout cancelled. No changes to your subscription.
          </div>
        )}
        {error && (
          <div style={styles.banner('#8B1A1A', '#fcecec')}>{error}</div>
        )}

        {current && (
          <div style={styles.currentPlan}>
            <div>
              <div style={styles.currentLabel}>Your current plan</div>
              <div style={styles.currentTier}>
                {current.tier_name || 'No active plan'}
                {current.tier_label ? <span style={styles.currentPrice}> · {current.tier_label}</span> : null}
              </div>
              {current.service_track === 'saas' && current.usage && (
                <div style={styles.usageRow}>
                  <UsageStat label="Credits" current={current.credits_total} limit={current.credits_monthly_allocation} />
                  <UsageStat label="Contacts" current={current.usage.contacts?.current} limit={current.usage.contacts?.limit} />
                  <UsageStat label="Users" current={current.usage.users?.current} limit={current.usage.users?.limit} />
                  <UsageStat label="Workspaces" current={current.usage.workspaces?.current} limit={current.usage.workspaces?.limit} />
                </div>
              )}
            </div>
            {current.has_stripe_customer && (
              <button
                onClick={handlePortal}
                disabled={actionLoading === 'portal'}
                style={styles.portalBtn}
              >
                {actionLoading === 'portal' ? 'Opening…' : 'Manage subscription'}
              </button>
            )}
          </div>
        )}

        {orderTracks(tierData || TIER_DATA).map(([trackKey, track]) => (
          <section key={trackKey} style={styles.section}>
            <h2 style={styles.h2}>{track.label}</h2>
            <p style={styles.sectionDescription}>{track.description || (TIER_DATA[trackKey]?.description) || ''}</p>
            <div style={styles.tierGrid}>
              {track.tiers.map((tier) => {
                const isCurrent = isCurrentTier(trackKey, tier.key);
                if (tier.popular === undefined) tier.popular = (tier.key === 'growth');
                const loadingKey = trackKey + '-' + tier.key;
                const isLoading = actionLoading === loadingKey || (hasSubscription && actionLoading === 'portal');
                let buttonLabel;
                if (isCurrent) {
                  buttonLabel = 'Current plan';
                } else if (isLoading) {
                  buttonLabel = hasSubscription ? 'Opening portal…' : 'Starting checkout…';
                } else if (hasSubscription) {
                  buttonLabel = 'Switch to ' + tier.name;
                } else {
                  buttonLabel = 'Subscribe to ' + tier.name;
                }
                return (
                  <div
                    key={tier.key}
                    style={{
                      ...styles.tierCard,
                      ...(isCurrent ? styles.tierCardCurrent : {}),
                      ...(tier.popular && !isCurrent ? styles.tierCardPopular : {}),
                    }}
                  >
                    {isCurrent && <div style={styles.tierBadge}>Your plan</div>}
                    {!isCurrent && tier.popular && <div style={styles.tierBadgePopular}>Most popular</div>}
                    <div style={styles.tierName}>{tier.name}</div>
                    <div style={styles.tierTagline}>{tier.tagline}</div>
                    <div style={styles.tierPriceRow}>
                      <span style={styles.tierPrice}>£{tier.price.toLocaleString('en-GB')}</span>
                      <span style={styles.tierPeriod}>/month</span>
                    </div>
                    {(() => {
                      const allFeatures = tier.features || tier.highlights || [];
                      const expandKey = trackKey + '-' + tier.key;
                      const isExpanded = !!expandedTiers[expandKey];
                      const visible = isExpanded ? allFeatures : allFeatures.slice(0, 5);
                      const hasMore = allFeatures.length > 5;
                      return (
                        <>
                          <ul style={styles.tierHighlights}>
                            {visible.map((h, i) => (
                              <li key={i} style={styles.tierHighlight}>
                                <span style={styles.tick}>✓</span> {h}
                              </li>
                            ))}
                          </ul>
                          {hasMore && (
                            <button
                              type="button"
                              onClick={() => setExpandedTiers(prev => ({ ...prev, [expandKey]: !prev[expandKey] }))}
                              style={{ background: 'none', border: 'none', color: '#8B1A1A', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0 16px', textAlign: 'left', marginLeft: 0 }}
                            >
                              {isExpanded ? '− Show less' : '+ Show all ' + allFeatures.length + ' features'}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    <button
                      onClick={() => handleTierAction(trackKey, tier.key)}
                      disabled={isCurrent || isLoading}
                      style={{
                        ...styles.tierBtn,
                        ...(isCurrent ? styles.tierBtnDisabled : {}),
                      }}
                    >
                      {buttonLabel}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function UsageStat({ label, current, limit }) {
  const limitDisplay = limit === null || limit === undefined ? 'Unlimited' : limit.toLocaleString('en-GB');
  const currentDisplay = (current ?? 0).toLocaleString('en-GB');
  const pct = limit && limit > 0 ? Math.min(100, ((current ?? 0) / limit) * 100) : 0;
  return (
    <div style={{ marginRight: 32, marginTop: 8 }}>
      <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>
        {currentDisplay} <span style={{ color: '#999', fontWeight: 400 }}>/ {limitDisplay}</span>
      </div>
      {limit && limit > 0 && (
        <div style={{ width: 120, height: 4, background: '#E8E4E0', borderRadius: 2, marginTop: 4 }}>
          <div style={{ width: pct + '%', height: '100%', background: pct > 90 ? '#8B1A1A' : '#1A1A1A', borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#FAFAF7', padding: '40px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  maxWidth: { maxWidth: 1200, margin: '0 auto' },
  loading: { padding: 40, textAlign: 'center', color: '#666', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  header: { marginBottom: 24, textAlign: 'center' },
  h1: { fontSize: 36, fontWeight: 700, margin: '0 0 8px', color: '#1A1A1A' },
  subhead: { fontSize: 16, color: '#666', margin: 0 },
  banner: (color, bg) => ({ background: bg, color, padding: '12px 16px', borderRadius: 6, marginBottom: 24, fontSize: 14 }),
  currentPlan: { background: '#fff', border: '1px solid #E8E4E0', borderRadius: 8, padding: 20, marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 },
  currentLabel: { fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 6 },
  currentTier: { fontSize: 22, fontWeight: 700, color: '#1A1A1A' },
  currentPrice: { color: '#666', fontWeight: 400, fontSize: 16 },
  usageRow: { display: 'flex', flexWrap: 'wrap', marginTop: 4 },
  portalBtn: { background: '#fff', color: '#1A1A1A', border: '1px solid #1A1A1A', padding: '10px 20px', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  section: { marginBottom: 48 },
  h2: { fontSize: 22, fontWeight: 700, color: '#1A1A1A', margin: '0 0 4px' },
  sectionDescription: { fontSize: 14, color: '#666', margin: '0 0 20px' },
  tierGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 },
  tierCard: { background: '#fff', border: '1px solid #E8E4E0', borderRadius: 8, padding: 24, position: 'relative', display: 'flex', flexDirection: 'column' },
  tierCardCurrent: { borderColor: '#8B1A1A', borderWidth: 2, padding: 23 },
  tierCardPopular: { borderColor: '#1A1A1A' },
  tierBadge: { position: 'absolute', top: -10, left: 16, background: '#8B1A1A', color: '#fff', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 },
  tierBadgePopular: { position: 'absolute', top: -10, left: 16, background: '#1A1A1A', color: '#fff', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6 },
  tierName: { fontSize: 22, fontWeight: 700, color: '#8B1A1A', marginBottom: 4 },
  tierTagline: { fontSize: 13, color: '#666', marginBottom: 16, minHeight: 32 },
  tierPriceRow: { marginBottom: 20, display: 'flex', alignItems: 'baseline' },
  tierPrice: { fontSize: 32, fontWeight: 700, color: '#1A1A1A' },
  tierPeriod: { fontSize: 14, color: '#666', marginLeft: 4 },
  tierHighlights: { listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 },
  tierHighlight: { fontSize: 14, color: '#1A1A1A', padding: '6px 0', display: 'flex', alignItems: 'flex-start', lineHeight: 1.4 },
  tick: { color: '#8B1A1A', fontWeight: 700, marginRight: 8, flexShrink: 0 },
  tierBtn: { background: '#8B1A1A', color: '#fff', border: 'none', padding: '12px 16px', borderRadius: 6, fontWeight: 600, fontSize: 14, cursor: 'pointer', width: '100%' },
  tierBtnDisabled: { background: '#E8E4E0', color: '#666', cursor: 'default' },
};
